import {
  DEPARTMENTS_BY_CODE,
  NAME_FIRST_YEAR,
  NAME_LAST_YEAR,
  NAME_MAP_FROM,
  namePath,
  type DepartmentPage as DepartmentData,
  type DepartmentsOverview,
  type Leader,
} from "@listou/shared/schemas/prenoms";
import { useState } from "react";

import { nameCards } from "../../lib/api";
import { BarRow, FranceMap } from "./charts";
import { BOYS, GIRLS, GRID, OTHER_COLOR, number, reignsOf } from "./format";
import {
  Breadcrumbs,
  card,
  DepartmentLink,
  Faq,
  NameLink,
  NamesLayout,
  PrenomsNav,
  Source,
  YearDepartmentLink,
} from "./layout";
import { colorsFor, LeaderLegend, Reigns, tally } from "./ranking";
import { ShareButton } from "./share";
import {
  breadcrumbJsonLd,
  datasetJsonLd,
  faqJsonLd,
  graph,
  rankingJsonLd,
} from "./structured-data";
import { DecadeGrid, DepartmentGrid } from "./year-pages";

const list = (names: string[]) =>
  names.length < 2 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} et ${names.at(-1) ?? ""}`;
const firstNames = (leaders: Leader[], size = 3) => list(leaders.slice(0, size).map((l) => l.name));

function LeaderColumn({
  title,
  color,
  leaders,
}: {
  title: string;
  color: string;
  leaders: Leader[];
}) {
  const best = leaders[0]?.count ?? 1;
  return (
    <section>
      <h3
        className="mb-2 flex items-center gap-2 border-b-2 pb-2 text-xl font-bold"
        style={{ borderColor: color }}
      >
        <span className="inline-block h-3 w-3 rounded-full" style={{ background: color }} />
        {title}
      </h3>
      <ol>
        {leaders.map((leader, index) => (
          <BarRow
            key={leader.slug}
            rank={index + 1}
            name={<NameLink slug={leader.slug}>{leader.name}</NameLink>}
            value={number(leader.count)}
            ratio={leader.count / best}
            color={color}
          />
        ))}
      </ol>
    </section>
  );
}

export const departmentsIndexHead = () => ({
  title: "Prénoms par département : les plus donnés près de chez vous · Listou",
  image: nameCards.page("departments", "og"),
  description: `Quels prénoms sont les plus donnés dans votre département ? Carte et classement des prénoms de fille et de garçon de ${NAME_MAP_FROM} à ${NAME_LAST_YEAR}, pour les 101 départements, d’après l’Insee.`,
  jsonLd: graph(
    breadcrumbJsonLd(
      [{ label: "Prénoms", to: "/prenoms" }, { label: "Par département" }],
      "/prenoms/departement",
    ),
    datasetJsonLd({
      name: "Prénoms les plus donnés par département",
      description:
        "Prénoms de fille et de garçon les plus donnés dans chaque département français.",
      coverage: `${NAME_MAP_FROM}/${NAME_LAST_YEAR}`,
    }),
  ),
});

export function DepartmentsIndexPage({ departments }: { departments: DepartmentsOverview }) {
  const [sex, setSex] = useState<"girls" | "boys">("girls");
  const pick = (entry: DepartmentsOverview[number]) => (sex === "girls" ? entry.girl : entry.boy);
  const byCode = new Map(departments.map((entry) => [entry.department.code, pick(entry)]));
  const counts = tally(departments.map(pick));
  const colors = colorsFor([...counts.keys()]);
  return (
    <NamesLayout>
      <Breadcrumbs items={[{ label: "Prénoms", to: "/prenoms" }, { label: "Par département" }]} />
      <header className="space-y-6">
        <PrenomsNav
          current="/prenoms/departement"
          share={
            <ShareButton
              path="/prenoms/departement"
              title="Les prénoms de chaque département"
              text="Quels prénoms sont les plus donnés près de chez vous ? Sur Listou :"
              card={(format) => nameCards.page("departments", format)}
              slug="prenoms-par-departement"
            />
          }
        />
        <h1 className="max-w-4xl text-4xl sm:text-6xl">Les prénoms de chaque département</h1>
        <p className="max-w-3xl text-lg text-muted">
          Un prénom populaire à Paris l’est-il autant à Brest ou à Marseille ? Pour chaque
          département, retrouvez les prénoms de fille et de garçon les plus donnés depuis{" "}
          {NAME_MAP_FROM}, puis le prénom n° 1 de chaque année depuis {NAME_FIRST_YEAR}.
        </p>
      </header>
      <section className={`${card} space-y-5`}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="text-3xl font-bold">Le prénom le plus donné depuis {NAME_MAP_FROM}</h2>
          <div className="flex gap-2" role="group" aria-label="Sexe">
            {(
              [
                ["girls", "Filles"],
                ["boys", "Garçons"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={sex === value}
                onClick={() => setSex(value)}
                className={`rounded-full border px-4 py-2 text-sm font-bold ${sex === value ? "border-ink bg-ink text-white" : "border-line"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid items-center gap-8 md:grid-cols-2">
          <FranceMap
            label="Prénom le plus donné par département"
            fill={(code) => {
              const leader = byCode.get(code);
              return leader ? (colors.get(leader.slug) ?? OTHER_COLOR) : GRID;
            }}
            describe={(code) =>
              `${DEPARTMENTS_BY_CODE.get(code)?.name ?? code} : ${byCode.get(code)?.name ?? "aucun effectif publié"}`
            }
            href={(code) => ({
              to: namePath.department(DEPARTMENTS_BY_CODE.get(code)?.slug ?? ""),
            })}
          />
          <LeaderLegend counts={counts} colors={colors} />
        </div>
      </section>
      <section className={`${card} space-y-3`}>
        <h2 className="text-2xl font-bold">Les 101 départements</h2>
        <div className="max-h-[40rem] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-surface">
              <tr>
                <th scope="col" className="py-2">
                  Département
                </th>
                <th scope="col">Fille n° 1 depuis {NAME_MAP_FROM}</th>
                <th scope="col">Garçon n° 1 depuis {NAME_MAP_FROM}</th>
              </tr>
            </thead>
            <tbody>
              {departments.map((entry) => (
                <tr key={entry.department.code} className="border-t border-line">
                  <th scope="row" className="py-2">
                    <DepartmentLink slug={entry.department.slug} className="underline">
                      {entry.department.name} ({entry.department.code})
                    </DepartmentLink>
                  </th>
                  <td>
                    {entry.girl ? (
                      <NameLink slug={entry.girl.slug}>{entry.girl.name}</NameLink>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {entry.boy ? <NameLink slug={entry.boy.slug}>{entry.boy.name}</NameLink> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <Source />
    </NamesLayout>
  );
}

const departmentFaq = (data: DepartmentData) => {
  const { department } = data;
  return [
    {
      question: `Quels sont les prénoms de fille les plus donnés en ${department.name} ?`,
      answer: `Depuis ${NAME_MAP_FROM}, ${firstNames(data.recent.girls, 5)}. Sur tout le XXe et le XXIe siècle : ${firstNames(data.all.girls, 5)}.`,
    },
    {
      question: `Quels sont les prénoms de garçon les plus donnés en ${department.name} ?`,
      answer: `Depuis ${NAME_MAP_FROM}, ${firstNames(data.recent.boys, 5)}. Sur tout le XXe et le XXIe siècle : ${firstNames(data.all.boys, 5)}.`,
    },
    {
      question: `Quel prénom est n° 1 en ${department.name} en ${NAME_LAST_YEAR} ?`,
      answer: `${data.leaders.at(-1)?.girl?.name ?? "Aucun prénom"} chez les filles et ${data.leaders.at(-1)?.boy?.name ?? "aucun prénom"} chez les garçons.`,
    },
  ];
};

export const departmentHead = (data: DepartmentData) => {
  const place = `${data.department.name} (${data.department.code})`;
  return {
    title: `Prénoms les plus donnés – ${place} · Listou`,
    image: nameCards.department(data.department.slug, "og"),
    description: `Les prénoms de fille et de garçon les plus donnés en ${data.department.name} : ${data.recent.girls[0]?.name ?? "—"} et ${data.recent.boys[0]?.name ?? "—"} depuis ${NAME_MAP_FROM}. Le n° 1 de chaque année depuis 1900, d’après l’Insee.`,
    jsonLd: graph(
      breadcrumbJsonLd(
        [
          { label: "Prénoms", to: "/prenoms" },
          { label: "Par département", to: "/prenoms/departement" },
          { label: data.department.name },
        ],
        namePath.department(data.department.slug),
      ),
      rankingJsonLd(`Prénoms de fille les plus donnés, ${place}`, data.recent.girls.slice(0, 20)),
      rankingJsonLd(`Prénoms de garçon les plus donnés, ${place}`, data.recent.boys.slice(0, 20)),
      faqJsonLd(departmentFaq(data)),
      datasetJsonLd({
        name: `Prénoms donnés en ${data.department.name}`,
        description: `Prénoms des enfants nés en ${place} depuis ${NAME_FIRST_YEAR}.`,
        coverage: `${NAME_FIRST_YEAR}/${NAME_LAST_YEAR}`,
        spatial: place,
      }),
    ),
  };
};

export function DepartmentPage({ data }: { data: DepartmentData }) {
  const { department } = data;
  const [period, setPeriod] = useState<"recent" | "all">("recent");
  const shown = data[period];
  const girlReigns = reignsOf(
    data.leaders.map((entry) => ({ year: entry.year, leader: entry.girl })),
  );
  const boyReigns = reignsOf(
    data.leaders.map((entry) => ({ year: entry.year, leader: entry.boy })),
  );
  const latest = data.leaders.at(-1);
  return (
    <NamesLayout>
      <Breadcrumbs
        items={[
          { label: "Prénoms", to: "/prenoms" },
          { label: "Par département", to: "/prenoms/departement" },
          { label: department.name },
        ]}
      />
      <header className="space-y-6">
        <PrenomsNav
          current="/prenoms/departement"
          share={
            <ShareButton
              path={namePath.department(department.slug)}
              title={`Les prénoms les plus donnés en ${department.name}`}
              text={`Les prénoms les plus donnés en ${department.name} sur Listou :`}
              card={(format) => nameCards.department(department.slug, format)}
              slug={`prenoms-${department.slug}`}
            />
          }
        />
        <h1 className="max-w-4xl text-4xl sm:text-6xl">
          Prénoms les plus donnés en {department.name} ({department.code})
        </h1>
        <p className="max-w-3xl text-lg text-muted">
          Depuis {NAME_MAP_FROM}, les prénoms de fille les plus donnés en {department.name} sont{" "}
          {firstNames(data.recent.girls)}. Chez les garçons : {firstNames(data.recent.boys)}.
          {latest?.girl && latest.boy
            ? ` En ${latest.year}, ${latest.girl.name} et ${latest.boy.name} sont en tête.`
            : ""}
        </p>
      </header>
      <section className={`${card} space-y-6`}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <h2 className="text-3xl font-bold">Le classement, filles et garçons</h2>
          <div className="flex gap-2" role="group" aria-label="Période">
            {(
              [
                ["recent", `Depuis ${NAME_MAP_FROM}`],
                ["all", `Depuis ${NAME_FIRST_YEAR}`],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={period === value}
                onClick={() => setPeriod(value)}
                className={`rounded-full border px-4 py-2 text-sm font-bold ${period === value ? "border-ink bg-ink text-white" : "border-line"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid gap-10 md:grid-cols-2">
          <LeaderColumn title="Filles" color={GIRLS} leaders={shown.girls} />
          <LeaderColumn title="Garçons" color={BOYS} leaders={shown.boys} />
        </div>
        <p className="text-sm text-muted">
          Naissances cumulées sur la période, effectifs arrondis à 5 par l’Insee. Les prénoms rares
          sont masqués dans le fichier départemental.
        </p>
      </section>
      <section className={`${card} space-y-8`}>
        <h2 className="text-3xl font-bold">Le prénom n° 1, année après année</h2>
        <div className="space-y-3">
          <h3 className="flex items-center gap-2 text-xl font-bold">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: GIRLS }} />
            Chez les filles
          </h3>
          <Reigns reigns={girlReigns} label={`Prénoms féminins n° 1 en ${department.name}`} />
        </div>
        <div className="space-y-3">
          <h3 className="flex items-center gap-2 text-xl font-bold">
            <span className="inline-block h-3 w-3 rounded-full" style={{ background: BOYS }} />
            Chez les garçons
          </h3>
          <Reigns reigns={boyReigns} label={`Prénoms masculins n° 1 en ${department.name}`} />
        </div>
      </section>
      <DecadeGrid
        title={`Le classement de ${department.name}, année par année`}
        link={(year) => (
          <YearDepartmentLink year={year} slug={department.slug}>
            {year}
          </YearDepartmentLink>
        )}
      />
      <DepartmentGrid
        title="Les autres départements"
        current={department.code}
        link={(slug, name) => (
          <DepartmentLink slug={slug} className="underline">
            {name}
          </DepartmentLink>
        )}
      />
      <Faq items={departmentFaq(data)} />
      <Source />
    </NamesLayout>
  );
}
